import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdSubscriptions, MdSearch, MdAcUnit } from 'react-icons/md';

import PageHeader from '../../../components/owner/PageHeader';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import TableRowIconActions from '../../../components/shared/TableRowIconActions';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  formatDateFilterValue,
  uniqueColumnOptions,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import ReceptionistMemberForm from './ReceptionistMemberForm';

import {
  packageOptions,
  formToMemberRecord,
  memberToFormData,
  getGymMemberId,
  getMemberCreatedAt,
} from '../../admin/members/memberData';

import TablePagination from '../../../components/shared/TablePagination';

import {
  fetchMembersPage,
  fetchMembersExport,
  fetchActiveSubscriptionMemberIdsFor,
  createMember,
  updateMember,
  deleteMember,
} from '../../../services/receptionist/receptionistApi';
import { getReceptionistBranch } from '../../../services/gym/branchContext';
import { pkTodayKey, pkTodayRange } from '../../../utils/pkTime';
import { usePanelToast } from '../../../context/PanelToastContext';
import { PAYMENTS_UPDATE_EVENT } from '../paymentStore';
import { RECEPTIONIST_BRANCH_EVENT } from '../../../services/gym/branchContext';

import '../../owner/ownerPages.css';

import '../../owner/subscriptions/subscriptions.css';

import '../../admin/members/members.css';
import '../../admin/adminListPages.css';

import './members.css';

import {

  printMemberList,

  exportMemberCsv,

  exportMemberPdf,

  exportMemberExcel,

  exportMembers,

} from './exportMembers';



function MemberManagement() {

  const navigate = useNavigate();
  const toast = usePanelToast();

  const [memberList, setMemberList] = useState([]);
  const [activeSubscriptionIds, setActiveSubscriptionIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  const requestIdRef = useRef(0);

  const [showAddModal, setShowAddModal] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [subscriptionTarget, setSubscriptionTarget] = useState(null);

  const [formKey, setFormKey] = useState(0);

  const [addInitialData, setAddInitialData] = useState({
    createdAt: pkTodayKey(),
    branch: getReceptionistBranch() || '',
  });

  const [subscriptionPackage, setSubscriptionPackage] = useState('Basic');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const todayRange = pkTodayRange();
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    gymId: '',
    name: '',
    email: '',
    mobile: '',
    branch: 'all',
    createdAt: '',
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedFilters]);

  const loadMembers = useCallback(async ({ silent = false } = {}) => {
    const reqId = ++requestIdRef.current;
    try {
      setError('');
      if (!silent) setLoading(true);
      const result = await fetchMembersPage({
        page,
        pageSize,
        search: debouncedSearch,
        filters: {
          ...debouncedFilters,
          rangeStart: todayRange.startDate,
          rangeEnd: todayRange.endDate,
          dateFrom: todayRange.startDate,
          dateTo: todayRange.endDate,
        },
      });
      if (reqId !== requestIdRef.current) return;
      const rows = result.rows || [];
      setMemberList(rows);
      setTotal(result.total || 0);
      if (result.fromRpc) {
        setActiveSubscriptionIds(new Set(rows.filter((m) => m.hasActiveSubscription).map((m) => m.id)));
      } else {
        const flags = await fetchActiveSubscriptionMemberIdsFor(rows.map((m) => m.id));
        if (reqId !== requestIdRef.current) return;
        setActiveSubscriptionIds(flags);
      }
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setError(err.message || 'Failed to load members');
      setMemberList([]);
      setTotal(0);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, debouncedFilters, todayRange.startDate, todayRange.endDate]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    const refresh = () => loadMembers({ silent: true });
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    return () => {
      window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
      window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    };
  }, [loadMembers]);

  const openAddModal = async () => {
    setFormKey((k) => k + 1);
    setAddInitialData({
      createdAt: pkTodayKey(),
      branch: getReceptionistBranch() || '',
    });
    setShowAddModal(true);
  };



  const closeAddModal = () => setShowAddModal(false);



  const openEditModal = (member) => {

    setFormKey((k) => k + 1);

    setEditTarget(member);

  };



  const closeEditModal = () => setEditTarget(null);

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    try {
      setDeleting(true);
      await deleteMember(deleteTarget.id);
      setDeleteTarget(null);
      toast.showDeleted('Member');
      await loadMembers();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete member');
    } finally {
      setDeleting(false);
    }
  };

  const openSubscriptionPage = (member) => {
    if (activeSubscriptionIds.has(member.id)) {
      toast.showError('This member already has an active subscription.');
      return;
    }
    navigate('/receptionist/subscriptions', {
      state: {
        customer: member.name,
        customerId: member.id,
        customerGymId: getGymMemberId(member) || '',
        // Do not force member.package — Sales picks a package they do not already have
      },
    });
  };

  const openFreezePage = (member) => {
    navigate('/receptionist/freeze', {
      state: {
        freezeMember: {
          id: member.id,
          name: member.name,
          gymId: getGymMemberId(member) || '',
          phone: member.mobile || member.phone || '',
          email: member.email || '',
          branch: member.branch || '',
          package: member.package || '',
          status: member.status || '',
        },
      },
    });
  };

  const closeSubscriptionModal = () => setSubscriptionTarget(null);



  const handleAddSubmit = async (form) => {

    try {
      const branch = getReceptionistBranch() || form.branch || '';
      await createMember({ ...form, branch });

      closeAddModal();
      setPage(1);
      await loadMembers({ silent: true });
      toast.showAdded('Member');
    } catch (err) {
      const message = err.message || 'Failed to add member';
      setError(message);
      toast.showError(message);
    }

  };



  const handleEditSubmit = async (form) => {

    if (!editTarget) return;

    try {
      const updated = await updateMember(editTarget.id, { ...form, gymId: editTarget.gymId });
      setMemberList((prev) =>
        prev.map((m) => (m.id === editTarget.id ? updated : m)),
      );
      closeEditModal();
      toast.showUpdated('Member');
    } catch (err) {
      const message = err.message || 'Failed to update member';
      setError(message);
      toast.showError(message);
    }

  };



  const handleSubscriptionUpdate = (e) => {

    e.preventDefault();

    if (!subscriptionTarget) return;

    setMemberList((prev) =>

      prev.map((m) =>

        m.id === subscriptionTarget.id ? { ...m, package: subscriptionPackage } : m,

      ),

    );

    closeSubscriptionModal();

  };



  const getEditInitialData = (member) => {
    const created = String(member?.joiningDate || member?.createdAt || '').slice(0, 10);
    return {
      ...memberToFormData(member),
      createdAt: /^\d{4}-\d{2}-\d{2}$/.test(created) ? created : '',
    };
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pageItems = memberList;

  const handleExport = async (runExport) => {
    try {
      const result = await fetchMembersExport({
        search: debouncedSearch,
        filters: {
          ...debouncedFilters,
          rangeStart: todayRange.startDate,
          rangeEnd: todayRange.endDate,
          dateFrom: todayRange.startDate,
          dateTo: todayRange.endDate,
        },
      });
      runExport(result.rows || []);
    } catch (err) {
      toast.showError(err.message || 'Export failed');
    }
  };

  return (

    <div>

      <PageHeader

        title="Members"

        subtitle="Register and manage gym members"

        variant="banner"

      >

        <button type="button" className="owner-btn owner-btn-primary" onClick={openAddModal}>

          Add Member

        </button>

      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading members...</p>}

      <PrintActionsHeader
        startDate={todayRange.startDate}
        endDate={todayRange.endDate}
        datesReadOnly
        onPrint={() => handleExport(printMemberList)}
        onCsv={() => handleExport(exportMemberCsv)}
        onPdf={() => handleExport(exportMemberPdf)}
        onExcel={() => handleExport(exportMemberExcel)}
        onExport={() => handleExport(exportMembers)}
        ariaLabel="Member print and export"
      >
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search name, serial, phone, or CNIC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </PrintActionsHeader>
      <p className="owner-placeholder-text owner-mb-16">
        {search.trim()
          ? 'Searching all dates in this branch'
          : 'Pakistan time · today only — members who joined, paid, or attended today'}
      </p>

      <div className="owner-card rec-member-list-card">
        <div className="owner-table-wrap">

          <table className="owner-table rec-member-table">

            <thead>

              <tr>

                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />

                <ColumnFilterHeader
                  label="Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search name..."
                />

                <ColumnFilterHeader
                  label="Email"
                  filterKey="email"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search email..."
                />

                <ColumnFilterHeader
                  label="Mobile"
                  filterKey="mobile"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />

                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(memberList, (m) => m.branch || '—')}
                  allLabel="All Branches"
                />

                <ColumnFilterHeader
                  label="Created At"
                  filterKey="createdAt"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />

                <th>Actions</th>

              </tr>

            </thead>

            <tbody>

              {pageItems.length > 0 ? (
                pageItems.map((member) => (
                  <tr key={member.id}>

                    <td className="rec-member-gym-id">{getGymMemberId(member) || '—'}</td>

                    <td>{member.name}</td>

                    <td>{member.email}</td>

                    <td>{member.mobile || member.phone}</td>

                    <td>{member.branch || '—'}</td>

                    <td>{getMemberCreatedAt(member)}</td>

                    <td>
                      <TableRowIconActions
                        onEdit={() => openEditModal(member)}
                        onDelete={() => setDeleteTarget(member)}
                        editLabel={`Edit ${member.name}`}
                        deleteLabel={`Delete ${member.name}`}
                      >
                        {!activeSubscriptionIds.has(member.id) && (
                          <button
                            type="button"
                            className="rec-member-icon-btn rec-member-icon-btn-subscription"
                            onClick={() => openSubscriptionPage(member)}
                            aria-label={`Subscription for ${member.name}`}
                            title="Subscription"
                          >
                            <MdSubscriptions />
                          </button>
                        )}
                        <button
                          type="button"
                          className="rec-member-icon-btn rec-member-icon-btn-freeze"
                          onClick={() => openFreezePage(member)}
                          aria-label={`Freeze ${member.name}`}
                          title="Freeze"
                        >
                          <MdAcUnit />
                        </button>
                      </TableRowIconActions>
                    </td>

                  </tr>

                ))

              ) : (

                <tr>

                  <td colSpan={7} className="owner-placeholder-text">
                    {memberList.length === 0
                      ? 'No members registered yet.'
                      : 'No members match your search.'}

                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>

        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />

      </div>



      {showAddModal && (

        <div className="subscription-modal-overlay" onClick={closeAddModal}>

          <div

            className="subscription-modal admin-form-modal"

            onClick={(e) => e.stopPropagation()}

            role="dialog"

            aria-modal="true"

            aria-labelledby="add-member-title"

          >

            <div className="subscription-modal-header">

              <h2 id="add-member-title" className="subscription-modal-title">Add Member</h2>

              <button type="button" className="subscription-modal-close" onClick={closeAddModal} aria-label="Close">×</button>

            </div>

            <ReceptionistMemberForm

              key={formKey}

              submitLabel="Add Member"

              onSubmit={handleAddSubmit}

              onCancel={closeAddModal}

              initialData={addInitialData}

            />

          </div>

        </div>

      )}



      {editTarget && (

        <div className="subscription-modal-overlay" onClick={closeEditModal}>

          <div

            className="subscription-modal admin-form-modal"

            onClick={(e) => e.stopPropagation()}

            role="dialog"

            aria-modal="true"

            aria-labelledby="edit-member-title"

          >

            <div className="subscription-modal-header">

              <h2 id="edit-member-title" className="subscription-modal-title">Edit Member</h2>

              <button type="button" className="subscription-modal-close" onClick={closeEditModal} aria-label="Close">×</button>

            </div>

            <ReceptionistMemberForm

              key={formKey}

              submitLabel="Save Changes"

              onSubmit={handleEditSubmit}

              onCancel={closeEditModal}

              initialData={getEditInitialData(editTarget)}

            />

          </div>

        </div>

      )}



      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Member</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete member <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {subscriptionTarget && (

        <div className="subscription-modal-overlay" onClick={closeSubscriptionModal}>

          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">

            <div className="subscription-modal-header">

              <h2 className="subscription-modal-title">Subscription</h2>

              <button type="button" className="subscription-modal-close" onClick={closeSubscriptionModal} aria-label="Close">×</button>

            </div>

            <div className="rec-member-slip">

              <div className="rec-member-slip-row"><span>Member</span><strong>{subscriptionTarget.name}</strong></div>

              <div className="rec-member-slip-row"><span>Serial No</span><strong>{getGymMemberId(subscriptionTarget) || '—'}</strong></div>

              <div className="rec-member-slip-row"><span>Current Package</span><strong>{subscriptionTarget.package}</strong></div>

              <div className="rec-member-slip-row"><span>Trainer</span><strong>{subscriptionTarget.trainer}</strong></div>

              <div className="rec-member-slip-row"><span>Joining Date</span><strong>{subscriptionTarget.joiningDate}</strong></div>

              <div className="rec-member-slip-row"><span>Status</span><strong>{subscriptionTarget.status === 'active' ? 'Active' : 'Inactive'}</strong></div>

            </div>

            <form className="subscription-form" onSubmit={handleSubscriptionUpdate}>

              <div className="owner-form-group">

                <label className="owner-form-label" htmlFor="subscription-package">Update Package</label>

                <select

                  id="subscription-package"

                  className="owner-form-select"

                  value={subscriptionPackage}

                  onChange={(e) => setSubscriptionPackage(e.target.value)}

                >

                  {packageOptions.map((pkg) => (

                    <option key={pkg} value={pkg}>{pkg}</option>

                  ))}

                </select>

              </div>

              <div className="subscription-form-actions">

                <button type="submit" className="owner-btn owner-btn-primary">Update Subscription</button>

                <Link to="/receptionist/subscriptions" className="owner-btn owner-btn-secondary">

                  Manage Subscriptions

                </Link>

                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeSubscriptionModal}>

                  Close

                </button>

              </div>

            </form>

          </div>

        </div>

      )}

    </div>

  );

}



export default MemberManagement;

